type LogType = 'info' | 'warn' | 'error' | 'success';

interface LogEntry {
    id: string;
    timestamp: string;
    type: LogType;
    message: string;
    data?: any;
}

const MAX_LOGS = 100;
const LOG_STORAGE_KEY = 'jointsession_logs';

class Logger {
    private logs: LogEntry[] = [];

    constructor() {
        this.loadLogs();
    }

    private loadLogs() {
        try {
            const stored = localStorage.getItem(LOG_STORAGE_KEY);
            if (stored) {
                this.logs = JSON.parse(stored);
            }
        } catch (e) {
            console.warn('Failed to load logs', e);
        }
    }

    private saveLogs() {
        try {
            localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(this.logs));
        } catch (e) {
            // Storage likely full or disabled
        }
    }

    private addLog(type: LogType, message: string, data?: any) {
        const entry: LogEntry = {
            id: crypto.randomUUID(),
            timestamp: new Date().toISOString(),
            type,
            message,
            data: data ? JSON.stringify(data, null, 2) : undefined
        };

        this.logs = [entry, ...this.logs].slice(0, MAX_LOGS);
        this.saveLogs();

        // Also echo to real console
        const color = type === 'error' ? 'red' : type === 'warn' ? 'orange' : type === 'success' ? 'green' : 'blue';
        console.log(`%c[${type.toUpperCase()}] ${message}`, `color:${color};font-weight:bold`, data || '');
    }

    info(message: string, data?: any) {
        this.addLog('info', message, data);
    }

    warn(message: string, data?: any) {
        this.addLog('warn', message, data);
    }

    error(message: string, data?: any) {
        this.addLog('error', message, data);
    }

    success(message: string, data?: any) {
        this.addLog('success', message, data);
    }

    getLogs() {
        return this.logs;
    }

    clear() {
        this.logs = [];
        this.saveLogs();
    }
}

export const logger = new Logger();
